// TODO: move to helpers folder
import { IS_DEMO, IS_INDIAN_EXCHANGE } from 'constants/constants';
import { COMPLETE_30_DAY_OFFER_ROLLOUT_DATE } from 'constants/dateTime';
import { type DateType, getDiff, timeNow, toUTC } from 'helpers/day';
import { isUserIdentityOrAddressVerified } from 'helpers/user';
import { isNan } from 'helpers/utils';
import IUser, { Banners, Profile } from 'types/IUser';

import { DAYS_ALLOWED_BETWEEN_KYC_AND_OFFER_CLAIM } from './UserJourneyCard.constants';
import { USER_JOURNEY_CARDS } from './UserJourneyCard.types';

const userRegisteredAfterComplete30DayOfferRollout = (
  registeredDate: Profile['registration_date']
) => {
  return (
    registeredDate &&
    getDiff(COMPLETE_30_DAY_OFFER_ROLLOUT_DATE, registeredDate, 'millisecond') > 0
  );
};

const showDoDepositCard = (depositInfo: Profile['deposit_info']) =>
  Number(depositInfo?.length) === 0;

const showDoTradeCard = (
  depositInfo: Profile['deposit_info'],
  last30DayTradingVolume: Profile['last_30_days_trading_volume']
) => Number(depositInfo?.length) > 0 && Number(last30DayTradingVolume) === 0;

interface ShowComplete30DayCardArgs {
  last30DayTradingVolume: Profile['last_30_days_trading_volume'];
  kycVerifiedOnDate: DateType;
  isKycDone: IUser['is_kyc_done'];
}

const showComplete30DayCard = ({
  last30DayTradingVolume,
  kycVerifiedOnDate,
  isKycDone,
}: ShowComplete30DayCardArgs) => {
  const currentDate = toUTC(timeNow());

  const kycRequirement =
    isKycDone &&
    getDiff(currentDate, kycVerifiedOnDate, 'days') <
      DAYS_ALLOWED_BETWEEN_KYC_AND_OFFER_CLAIM;

  if (IS_INDIAN_EXCHANGE) return kycRequirement;

  const valid30DayTradingVolume = !isNan(last30DayTradingVolume);

  return kycRequirement && valid30DayTradingVolume && Number(last30DayTradingVolume) > 0;
};

const bannerAlreadyViewed = (banners: Banners, key: USER_JOURNEY_CARDS) =>
  banners && key in banners;

interface CardToDisplayForUserJourneyArgs {
  depositInfo: Profile['deposit_info'];
  registrationDate: Profile['registration_date'];
  loginCount?: Profile['login_count'];
  last30DayTradingVolume: Profile['last_30_days_trading_volume'];
  kycVerifiedOn: IUser['kyc_verified_on'];
  isKycDone: IUser['is_kyc_done'];
  proofOfIdentityStatus: IUser['proof_of_identity_status'];
  proofOfAddressStatus: IUser['proof_of_identity_status'];
  loggedIn: boolean;
  banners: Banners;
  isBankAccountAdded: boolean;
  showDoKycCard: boolean;
}

const cardToDisplayForUserJourney = ({
  depositInfo,
  isKycDone,
  kycVerifiedOn,
  last30DayTradingVolume,
  banners,
  proofOfAddressStatus,
  proofOfIdentityStatus,
  registrationDate,
  loggedIn,
  isBankAccountAdded,
  showDoKycCard,
}: CardToDisplayForUserJourneyArgs): USER_JOURNEY_CARDS | null => {
  if (!loggedIn || userRegisteredAfterComplete30DayOfferRollout(registrationDate)) {
    return null;
  }

  if (showDoKycCard && !bannerAlreadyViewed(banners, USER_JOURNEY_CARDS.KYC)) {
    return USER_JOURNEY_CARDS.KYC;
  }

  if (
    isUserIdentityOrAddressVerified(proofOfIdentityStatus, proofOfAddressStatus) &&
    !isBankAccountAdded &&
    !bannerAlreadyViewed(banners, USER_JOURNEY_CARDS.ADD_BANK) &&
    IS_INDIAN_EXCHANGE &&
    !IS_DEMO
  ) {
    return USER_JOURNEY_CARDS.ADD_BANK;
  }

  let shouldShowDepositCard =
    isUserIdentityOrAddressVerified(proofOfIdentityStatus, proofOfAddressStatus) &&
    showDoDepositCard(depositInfo) &&
    !bannerAlreadyViewed(banners, USER_JOURNEY_CARDS.DEPOSIT);

  if (IS_INDIAN_EXCHANGE) {
    shouldShowDepositCard = shouldShowDepositCard && isBankAccountAdded;
  }

  if (shouldShowDepositCard) {
    return USER_JOURNEY_CARDS.DEPOSIT;
  }

  if (
    isUserIdentityOrAddressVerified(proofOfIdentityStatus, proofOfAddressStatus) &&
    showDoTradeCard(depositInfo, last30DayTradingVolume) &&
    !bannerAlreadyViewed(banners, USER_JOURNEY_CARDS.TRADE) &&
    !IS_INDIAN_EXCHANGE
  ) {
    return USER_JOURNEY_CARDS.TRADE;
  }

  let shouldShow30DaysCard =
    kycVerifiedOn &&
    !bannerAlreadyViewed(banners, USER_JOURNEY_CARDS.THIRTY_DAYS) &&
    (showComplete30DayCard({
      last30DayTradingVolume,
      isKycDone,
      kycVerifiedOnDate: toUTC(kycVerifiedOn as DateType),
    }) as boolean);

  if (IS_INDIAN_EXCHANGE) {
    shouldShow30DaysCard = shouldShow30DaysCard && Number(depositInfo?.length) > 0;
  }

  if (shouldShow30DaysCard) {
    return USER_JOURNEY_CARDS.THIRTY_DAYS;
  }

  return null;
};

export { cardToDisplayForUserJourney };
